import React, { useState, useEffect } from "react";
import { TfiClose } from "react-icons/tfi";
import { PiDotsThreeOutlineVertical, PiCrownLight } from "react-icons/pi";
import {
  FiEdit2,
  FiUserPlus,
  FiTrash2,
  FiShield,
  FiLogOut,
} from "react-icons/fi";
import { IoPersonRemoveOutline } from "react-icons/io5";
import { RiEarthLine, RiLockLine } from "react-icons/ri";
import styles from "../../../style/Pages/PopUp/ViewChannelPopUp.module.css";
import {
  useEditChannel,
  useEditChannelAction,
  useFetchChannelByid,
} from "../../../hooks/useChannel";
import { getUserDetails } from "../../../utils/auth";
import InvitePopup from "./InvitePopUp";
import LeaveConfirmation from "../../../components/ui/Warning/LeaveChannel";
import { getRegisterId } from "../../../utils/authId";
import FallbackAvatar from "../../../components/ui/FallbackAvatar/FallbackAvatar";
import { channelEdit } from "../../../routes/AccessCondition";
const apiUrl = process.env.REACT_APP_API_URL;

// Validation functions
const validateChannelName = (name) => {
  if (!name || name.trim().length === 0) {
    return "Channel name is required";
  }

  // Remove the # prefix for validation
  const nameWithoutHash = name.startsWith("#") ? name.substring(1) : name;

  if (nameWithoutHash.length < 2) {
    return "Channel name must be at least 2 characters long";
  }

  if (nameWithoutHash.length > 80) {
    return "Channel name cannot exceed 80 characters";
  }

  if (!/^[a-zA-Z0-9\s\-_]+$/.test(nameWithoutHash)) {
    return "Channel name can only contain letters, numbers, spaces, hyphens, and underscores";
  }

  return null;
};

const validateTopic = (topic) => {
  if (topic && topic.length > 250) {
    return "Topic cannot exceed 250 characters";
  }
  return null;
};

const validateDescription = (description) => {
  if (description && description.length > 1000) {
    return "Description cannot exceed 1000 characters";
  }
  return null;
};

const SkeletonText = ({ width = "100%", height = "1rem" }) => (
  <div className={styles.skeleton} style={{ width, height }}></div>
);

const SkeletonAvatar = () => <div className={styles.skeletonAvatar}></div>;

const SkeletonButton = () => <div className={styles.skeletonButton}></div>;

const SkeletonTabContent = () => (
  <div className={styles.skeletonContainer}>
    {/* About Tab Skeleton */}
    <div className={styles.skeletonSection}>
      <SkeletonText width="30%" height="1.2rem" />
      <SkeletonText width="80%" />
      <SkeletonText width="60%" />
    </div>
    <div className={styles.skeletonSection}>
      <SkeletonText width="30%" height="1.2rem" />
      <SkeletonText width="90%" />
      <SkeletonText width="70%" />
      <SkeletonText width="50%" />
    </div>
    <div className={styles.skeletonSection}>
      <SkeletonText width="30%" height="1.2rem" />
      <SkeletonText width="40%" />
    </div>
  </div>
);

const SkeletonMembersContent = () => (
  <div className={styles.skeletonContainer}>
    <div className={styles.skeletonSection}>
      <div className={styles.skeletonFlexBetween}>
        <SkeletonText width="30%" height="1.2rem" />
        <SkeletonButton width="80px" height="32px" />
      </div>
    </div>
    {[1, 2, 3].map((item) => (
      <div key={item} className={styles.skeletonMemberCard}>
        <div className={styles.skeletonFlex}>
          <SkeletonAvatar />
          <div className={styles.skeletonMemberInfo}>
            <SkeletonText width="60%" height="1rem" />
            <SkeletonText width="40%" height="0.8rem" />
          </div>
        </div>
        <SkeletonText width="24px" height="24px" />
      </div>
    ))}
  </div>
);

const SkeletonSettingsContent = () => (
  <div className={styles.skeletonContainer}>
    <div className={styles.skeletonSection}>
      <SkeletonText width="40%" height="1.2rem" />
      {[1, 2].map((item) => (
        <div key={item} className={styles.skeletonSettingItem}>
          <div className={styles.skeletonFlex}>
            <SkeletonAvatar size="24px" />
            <div className={styles.skeletonSettingInfo}>
              <SkeletonText width="50%" height="1rem" />
              <SkeletonText width="70%" height="0.8rem" />
            </div>
          </div>
          <SkeletonButton width="100px" height="32px" />
        </div>
      ))}
    </div>
  </div>
);

const ViewChannelPopUp = ({ onClose, channelId, updated }) => {
  const {
    channel,
    loading: channelLoading,
    refresh: refreshChannel,
  } = useFetchChannelByid(channelId);
  const { editChannel, isLoading: isEditLoading } = useEditChannel(channelId);
  const {
    editActionChannel,
    //  isLoading: isUpdateLoading
  } = useEditChannelAction(channelId);
  const user = getUserDetails() || {};
  const otherUserEdit = channelEdit();

  const {
    id,
    company,
    channel_name,
    channel_topic,
    description,
    create_date,
    channel_manager_details = [],
    channel_people_details = [],
  } = channel[0] || {};

  useEffect(() => {
    if (channel[0]) {
      setEditValues({
        topic: channel[0].channel_topic || "",
        description: channel[0].description || "",
      });
      setNewChannelName(channel[0].channel_name || "");
    }
  }, [channel]);

  const loggedInUserId = getRegisterId();
  const isManager = channel_manager_details.some(
    (m) => m.id === loggedInUserId
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const [newChannelName, setNewChannelName] = useState(channel_name || "");
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [postingPermission, setPostingPermission] = useState("all_people");
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [activeTab, setActiveTab] = useState("about");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isEditing, setIsEditing] = useState({
    topic: false,
    description: false,
  });
  const [editValues, setEditValues] = useState({
    topic: channel_topic,
    description: description,
  });
  const [imageErrorMap, setImageErrorMap] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [actionInProgress, setActionInProgress] = useState(false);

  const toggleDropdown = (userId) => {
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };

  useEffect(() => {
    if (channel[0]) {
      setPostingPermission(channel[0].allow_access || "all_people");
    }
  }, [channel]);

  const validateForm = (field, value) => {
    let errors = { ...validationErrors };

    switch (field) {
      case "channelName":
        const nameError = validateChannelName(value);
        if (nameError) {
          errors.channelName = nameError;
        } else {
          delete errors.channelName;
        }
        break;
      case "topic":
        const topicError = validateTopic(value);
        if (topicError) {
          errors.topic = topicError;
        } else {
          delete errors.topic;
        }
        break;
      case "description":
        const descError = validateDescription(value);
        if (descError) {
          errors.description = descError;
        } else {
          delete errors.description;
        }
        break;
      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleMakeManager = async (userId) => {
    if (actionInProgress) return;

    setActionInProgress(true);
    try {
      await editActionChannel({ channel_manager: [userId] });
      refreshChannel();
    } catch (error) {
      console.error("Failed to make manager:", error);
    } finally {
      setActionInProgress(false);
      setActiveDropdown(null);
    }
  };

  const handleSaveChannelName = async () => {
    if (isEditLoading || actionInProgress) return;

    // Validate channel name
    if (!validateForm("channelName", newChannelName)) {
      return;
    }

    if (!newChannelName.trim()) return;

    setActionInProgress(true);
    try {
      await editChannel({ channel_name: newChannelName });
      setIsEditingName(false);
      updated();
      refreshChannel();
    } catch (error) {
      console.error("Failed to save channel name:", error);
      setNewChannelName(channel_name);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleAsMember = async (userId) => {
    if (actionInProgress) return;

    setActionInProgress(true);
    try {
      await editActionChannel({ leave_channel_manager: userId });
      refreshChannel();
      updated();
    } catch (error) {
      console.error("Failed to remove manager:", error);
    } finally {
      setActionInProgress(false);
      setActiveDropdown(null);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (actionInProgress) return;

    // Prevent removing the last manager
    if (
      channel_manager_details.length <= 1 &&
      channel_manager_details.some((m) => m.id === userId)
    ) {
      alert("Cannot remove the last manager from the channel");
      setActiveDropdown(null);
      return;
    }

    setActionInProgress(true);
    try {
      await editActionChannel({ leave_people: userId });
      refreshChannel();
      updated();
    } catch (error) {
      console.error("Failed to remove member:", error);
    } finally {
      setActionInProgress(false);
      setActiveDropdown(null);
    }
  };

  const handleEditField = (field) => {
    setIsEditing({ ...isEditing, [field]: true });
    // Clear any previous validation errors for this field
    setValidationErrors({ ...validationErrors, [field]: null });
  };

  const handleSaveEdit = async (field) => {
    if (isEditLoading || actionInProgress) return;

    // Validate the field
    if (!validateForm(field, editValues[field])) {
      return;
    }

    setActionInProgress(true);
    try {
      const updates = {};
      if (field === "topic") updates.channel_topic = editValues.topic;
      if (field === "description") updates.description = editValues.description;

      await editChannel(updates);
      setIsEditing({ ...isEditing, [field]: false });
      refreshChannel();
      updated();
    } catch (error) {
      console.error(`Failed to save ${field}:`, error);
    } finally {
      setActionInProgress(false);
    }
  };

  // const handleCancelEdit = (field, originalValue) => {
  //   setIsEditing({ ...isEditing, [field]: false });
  //   setEditValues({ ...editValues, [field]: originalValue });
  //   // Clear validation errors for this field
  //   setValidationErrors({ ...validationErrors, [field]: null });
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";

    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      return "Invalid date";
    }
  };

  const handlePostingPermissionChange = async (e) => {
    if (isEditLoading || actionInProgress) return;

    const newPermission = e.target.value;
    setPostingPermission(newPermission);

    setActionInProgress(true);
    try {
      await editChannel({ allow_access: newPermission });
      refreshChannel();
      updated();
    } catch (error) {
      console.error("Failed to update posting permission:", error);
      setPostingPermission(channel[0]?.allow_access || "all_people");
    } finally {
      setActionInProgress(false);
    }
  };

  const handleUpdated = () => {
    refreshChannel();
  };

  const handleUpdate = () => {
    onClose();
    updated();
  };

  const renderTabContent = () => {
    if (channelLoading) {
      switch (activeTab) {
        case "about":
          return <SkeletonTabContent />;
        case "members":
          return <SkeletonMembersContent />;
        case "settings":
          return <SkeletonSettingsContent />;
        default:
          return null;
      }
    }

    switch (activeTab) {
      case "about":
        return (
          <div className={styles.aboutContent}>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Topic</h4>
                {user.role !== "staff" &&
                  (user.role === "admin" || isManager ? (
                    <>
                      <button
                        className={styles.editButton}
                        onClick={() =>
                          isEditing.topic
                            ? handleSaveEdit("topic")
                            : handleEditField("topic")
                        }
                        disabled={isEditLoading || actionInProgress}
                      >
                        {isEditLoading && actionInProgress
                          ? "Saving..."
                          : isEditing.topic
                          ? "Save"
                          : "Edit"}
                      </button>
                    </>
                  ) : (
                    user.role === "other_user" ||
                    (isManager && otherUserEdit && (
                      <>
                        <button
                          className={styles.editButton}
                          onClick={() =>
                            isEditing.topic
                              ? handleSaveEdit("topic")
                              : handleEditField("topic")
                          }
                          disabled={isEditLoading || actionInProgress}
                        >
                          {isEditLoading && actionInProgress
                            ? "Saving..."
                            : isEditing.topic
                            ? "Save"
                            : "Edit"}
                        </button>
                      </>
                    ))
                  ))}
              </div>
              {isEditing.topic ? (
                <div>
                  <input
                    type="text"
                    value={editValues.topic}
                    onChange={(e) => {
                      setEditValues({ ...editValues, topic: e.target.value });
                      validateForm("topic", e.target.value);
                    }}
                    className={`${styles.editInput} ${
                      validationErrors.topic ? styles.inputError : ""
                    }`}
                    autoFocus
                    maxLength={250}
                  />
                  {validationErrors.topic && (
                    <div className={styles.errorText}>
                      {validationErrors.topic}
                    </div>
                  )}
                  <div className={styles.charCount}>
                    {editValues.topic.length}/250
                  </div>
                </div>
              ) : (
                <p className={styles.sectionText}>
                  {channel_topic || "No topic set"}
                </p>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Description</h4>
                {user.role !== "staff" &&
                  (user.role === "admin" || isManager ? (
                    <>
                      <button
                        className={styles.editButton}
                        onClick={() =>
                          isEditing.description
                            ? handleSaveEdit("description")
                            : handleEditField("description")
                        }
                        disabled={isEditLoading || actionInProgress}
                      >
                        {isEditLoading && actionInProgress
                          ? "Saving..."
                          : isEditing.description
                          ? "Save"
                          : "Edit"}
                      </button>
                    </>
                  ) : (
                    user.role === "other_user" ||
                    (isManager && otherUserEdit && (
                      <>
                        <button
                          className={styles.editButton}
                          onClick={() =>
                            isEditing.description
                              ? handleSaveEdit("description")
                              : handleEditField("description")
                          }
                          disabled={isEditLoading || actionInProgress}
                        >
                          {isEditLoading && actionInProgress
                            ? "Saving..."
                            : isEditing.description
                            ? "Save"
                            : "Edit"}
                        </button>
                      </>
                    ))
                  ))}
              </div>
              {isEditing.description ? (
                <div>
                  <textarea
                    value={editValues.description}
                    onChange={(e) => {
                      setEditValues({
                        ...editValues,
                        description: e.target.value,
                      });
                      validateForm("description", e.target.value);
                    }}
                    className={`${styles.editTextarea} ${
                      validationErrors.description ? styles.inputError : ""
                    }`}
                    autoFocus
                    maxLength={1000}
                  />
                  {validationErrors.description && (
                    <div className={styles.errorText}>
                      {validationErrors.description}
                    </div>
                  )}
                  <div className={styles.charCount}>
                    {editValues.description.length}/1000
                  </div>
                </div>
              ) : (
                <p className={styles.sectionText}>
                  {description || "No description provided"}
                </p>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Created</h4>
              </div>
              <p className={styles.sectionText}>{formatDate(create_date)}</p>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Channel Type</h4>
              </div>
              <div className={styles.channelType}>
                {channel.is_private ? (
                  <>
                    <RiLockLine className={styles.lockIcon} />
                    <span>Private channel</span>
                  </>
                ) : (
                  <>
                    <RiEarthLine className={styles.globeIcon} />
                    <span>Public channel</span>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case "members":
        return (
          <div className={styles.membersContent}>
            <div className={styles.membersHeader}>
              <h4>{channel_people_details?.length || 0} Members</h4>
              {user.role !== "staff" &&
                (user.role === "admin" || isManager ? (
                  <>
                    <button
                      className={styles.inviteButton}
                      onClick={() => setShowInvitePopup(true)}
                      disabled={actionInProgress}
                    >
                      <FiUserPlus size={16} />
                      <span>Invite</span>
                    </button>
                  </>
                ) : (
                  (user.role === "other_user" && otherUserEdit) ||
                  (isManager && (
                    <>
                      <button
                        className={styles.inviteButton}
                        onClick={() => setShowInvitePopup(true)}
                        disabled={actionInProgress}
                      >
                        <FiUserPlus size={16} />
                        <span>Invite</span>
                      </button>
                    </>
                  ))
                ))}
            </div>

            <div className={styles.membersList}>
              {channel_people_details?.map((member) => (
                <div key={member.id} className={styles.memberCard}>
                  <div className={styles.memberInfo}>
                    <div className={styles.avatarContainer}>
                      {imageErrorMap[member.id] ? (
                        <FallbackAvatar
                          name={`${member.first_name} ${member.last_name}`}
                          size={36}
                        />
                      ) : (
                        <img
                          src={`${apiUrl}${member.emp_pic}`}
                          alt={`${member.first_name} ${member.last_name}`}
                          className={styles.avatar}
                          onError={() =>
                            setImageErrorMap((prev) => ({
                              ...prev,
                              [member.id]: true,
                            }))
                          }
                        />
                      )}
                      <div
                        className={
                          member.online_status
                            ? styles.statusOnline
                            : styles.statusOffline
                        }
                      />
                    </div>
                    <div className={styles.memberDetails}>
                      <h5>
                        {member.first_name} {member.last_name}
                        {member.id === loggedInUserId && (
                          <span className={styles.youBadge}>You</span>
                        )}
                      </h5>
                      <p className={styles.memberRole}>
                        {member.about || "Member"}
                      </p>
                    </div>
                  </div>

                  <div className={styles.memberActions}>
                    {channel_manager_details.some(
                      (m) => m.id === member.id
                    ) && (
                      <div className={styles.managerBadge}>
                        <PiCrownLight size={16} />
                        <span>Manager</span>
                      </div>
                    )}
                    {user.role !== "staff" &&
                      member.id !== loggedInUserId &&
                      (user.role === "admin" || isManager ? (
                        <>
                          <div className={styles.memberMenu}>
                            <button
                              className={styles.menuButton}
                              onClick={() => toggleDropdown(member.id)}
                              disabled={actionInProgress}
                            >
                              <PiDotsThreeOutlineVertical size={18} />
                            </button>

                            {activeDropdown === member.id && (
                              <div className={styles.dropdownMenu}>
                                {!channel_manager_details.some(
                                  (m) => m.id === member.id
                                ) && (
                                  <button
                                    className={styles.dropdownItem}
                                    onClick={() => handleMakeManager(member.id)}
                                    disabled={actionInProgress}
                                  >
                                    <FiShield size={14} />
                                    <span>
                                      {actionInProgress
                                        ? "Processing..."
                                        : "Make manager"}
                                    </span>
                                  </button>
                                )}
                                {channel_manager_details.some(
                                  (m) => m.id === member.id
                                ) && (
                                  <button
                                    className={`${styles.dropdownItem} ${styles.dangerItem}`}
                                    onClick={() => handleAsMember(member.id)}
                                    disabled={actionInProgress}
                                  >
                                    <FiTrash2 size={14} />
                                    <span>
                                      {actionInProgress
                                        ? "Processing..."
                                        : "Remove as manager"}
                                    </span>
                                  </button>
                                )}
                                <button
                                  className={`${styles.dropdownItem} ${styles.dangerItem}`}
                                  onClick={() => handleRemoveMember(member.id)}
                                  disabled={actionInProgress}
                                >
                                  <IoPersonRemoveOutline size={14} />
                                  <span>
                                    {actionInProgress
                                      ? "Processing..."
                                      : "Remove member"}
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        (user.role === "other_user" &&
                          otherUserEdit &&
                          member.id !== loggedInUserId) ||
                        (isManager && (
                          <>
                            <div className={styles.memberMenu}>
                              <button
                                className={styles.menuButton}
                                onClick={() => toggleDropdown(member.id)}
                                disabled={actionInProgress}
                              >
                                <PiDotsThreeOutlineVertical size={18} />
                              </button>

                              {activeDropdown === member.id && (
                                <div className={styles.dropdownMenu}>
                                  {!channel_manager_details.some(
                                    (m) => m.id === member.id
                                  ) && (
                                    <button
                                      className={styles.dropdownItem}
                                      onClick={() =>
                                        handleMakeManager(member.id)
                                      }
                                      disabled={actionInProgress}
                                    >
                                      <FiShield size={14} />
                                      <span>
                                        {actionInProgress
                                          ? "Processing..."
                                          : "Make manager"}
                                      </span>
                                    </button>
                                  )}
                                  {channel_manager_details.some(
                                    (m) => m.id === member.id
                                  ) && (
                                    <button
                                      className={`${styles.dropdownItem} ${styles.dangerItem}`}
                                      onClick={() => handleAsMember(member.id)}
                                      disabled={actionInProgress}
                                    >
                                      <FiTrash2 size={14} />
                                      <span>
                                        {actionInProgress
                                          ? "Processing..."
                                          : "Remove as manager"}
                                      </span>
                                    </button>
                                  )}
                                  <button
                                    className={`${styles.dropdownItem} ${styles.dangerItem}`}
                                    onClick={() =>
                                      handleRemoveMember(member.id)
                                    }
                                    disabled={actionInProgress}
                                  >
                                    <IoPersonRemoveOutline size={14} />
                                    <span>
                                      {actionInProgress
                                        ? "Processing..."
                                        : "Remove member"}
                                    </span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        ))
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "settings":
        return (
          <div className={styles.settingsContent}>
            {/* {isManager ? (
              <> */}
            <div className={styles.settingGroup}>
              <h4>Channel Preferences</h4>

              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <FiShield className={styles.settingIcon} />
                  <div>
                    <h5>Posting Permissions</h5>
                    <p>Who can post</p>
                  </div>
                </div>
                <select
                  className={styles.settingSelect}
                  value={postingPermission}
                  onChange={handlePostingPermissionChange}
                  disabled={
                    isEditLoading ||
                    actionInProgress ||
                    !(
                      user.role !== "staff" &&
                      (user.role === "admin" ||
                        (user.role === "other_user" && otherUserEdit) ||
                        isManager)
                    )
                  }
                >
                  <option value="all_people">All members</option>
                  <option value="only_manager">Only managers</option>
                </select>
              </div>
            </div>
            {!isManager && (
              <div className={`${styles.settingGroup} ${styles.dangerZone}`}>
                <h4>Danger Zone</h4>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <FiLogOut className={styles.settingIcon} />
                    <div>
                      <h5>Leave Channel</h5>
                      <p>You will no longer be part of this channel.</p>
                    </div>
                  </div>
                  <button
                    className={styles.deleteButton}
                    onClick={() => setShowLeaveConfirmation(true)}
                    disabled={actionInProgress}
                  >
                    Leave Channel
                  </button>
                </div>
              </div>
            )}
            {/* </>
            ) : (
              <div className={styles.noPermissions}>
                <FiShield size={24} />
                <h4>Manager permissions required</h4>
                <p>Only channel managers can view and edit these settings</p>
              </div>
            )} */}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            {channelLoading ? (
              <div className={styles.skeletonFlex}>
                <SkeletonText width="120px" height="1.5rem" />
                <SkeletonText width="80px" height="1.2rem" />
              </div>
            ) : isEditingName ? (
              <div className={styles.nameEditContainer}>
                <div>
                  <input
                    type="text"
                    value={newChannelName}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Ensure it always starts with '#'
                      if (!value.startsWith("#")) {
                        value = "#" + value.replace(/^#*/, ""); // Remove any extra '#' and add one
                      }

                      setNewChannelName(value);
                      validateForm("channelName", value);
                    }}
                    className={`${styles.channelNameInput} ${
                      validationErrors.channelName ? styles.inputError : ""
                    }`}
                    autoFocus
                    maxLength={81} // 80 chars + # prefix
                  />
                  {validationErrors.channelName && (
                    <div className={styles.errorText}>
                      {validationErrors.channelName}
                    </div>
                  )}
                </div>

                <div className={styles.nameEditButtons}>
                  <button
                    className={styles.saveNameButton}
                    onClick={handleSaveChannelName}
                    disabled={
                      isEditLoading ||
                      actionInProgress ||
                      !!validationErrors.channelName
                    }
                  >
                    {isEditLoading && actionInProgress ? "Saving..." : "Save"}
                  </button>
                  <button
                    className={styles.cancelNameButton}
                    onClick={() => {
                      setIsEditingName(false);
                      setNewChannelName(channel_name);
                      setValidationErrors({
                        ...validationErrors,
                        channelName: null,
                      });
                    }}
                    disabled={isEditLoading || actionInProgress}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <h2 className={styles.channelnameh2}>{channel_name}</h2>
            )}
            {user.role !== "staff" &&
              (user.role === "admin" || isManager ? (
                <>
                  {!isEditingName && (
                    <button
                      className={styles.editNameButton}
                      onClick={() => setIsEditingName(true)}
                      disabled={isEditLoading || actionInProgress}
                    >
                      <FiEdit2 size={14} />
                    </button>
                  )}
                </>
              ) : (
                user.role === "other_user" ||
                (isManager && otherUserEdit && (
                  <>
                    {!isEditingName && (
                      <button
                        className={styles.editNameButton}
                        onClick={() => setIsEditingName(true)}
                        disabled={isEditLoading || actionInProgress}
                      >
                        <FiEdit2 size={14} />
                      </button>
                    )}
                  </>
                ))
              ))}
            {isManager && (
              <div className={styles.managerTag}>
                <PiCrownLight size={14} />
                <span>Manager</span>
              </div>
            )}
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            disabled={actionInProgress}
          >
            <TfiClose size={18} />
          </button>
        </div>

        <div className={styles.tabBar}>
          <button
            className={`${styles.tabButton} ${
              activeTab === "about" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("about")}
            disabled={actionInProgress}
          >
            About
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === "members" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("members")}
            disabled={actionInProgress}
          >
            Members
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === "settings" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("settings")}
            disabled={actionInProgress}
          >
            Settings
          </button>
        </div>

        <div className={styles.tabContent}>{renderTabContent()}</div>
      </div>
      {showLeaveConfirmation && (
        <LeaveConfirmation
          itemName={channel_name}
          channelId={id}
          onClose={() => setShowLeaveConfirmation(false)}
          update={handleUpdate}
        />
      )}
      {showInvitePopup && (
        <InvitePopup
          onClose={() => setShowInvitePopup(false)}
          Id={id}
          company={company}
          Updated={handleUpdated}
          OpenFrom={"channel"}
          PeopleId={channel_people_details}
        />
      )}
    </div>
  );
};

export default ViewChannelPopUp;
